import type { ChartValues } from '@/types/chart';
import type { StatisticsSeries } from '@/types/series';
import type { StatisticsData } from '@/types/statisticsApi';
import type { Compare } from '../types';
import { getPeriodLabel } from './getPeriodLabel';

export type StatisticsChartData<Series extends StatisticsSeries> = Record<Series, ChartValues[]>;
export const formatToChart = <Series extends StatisticsSeries, C extends Compare>(
  data: StatisticsData<Series, C>,
  series: Series[],
  label?: string,
) => {
  const chartData = series.reduce<StatisticsChartData<Series>>(
    (prev, curr) => {
      // Cast preserves the original object-literal behavior: `{ ...prev, [curr]: X }` never
      // type-checked X against ChartValues[] (computed keys in a spread literal bypass the
      // check). The producer's value/delta (number | null) and granularity (string) don't match
      // Data exactly; re-typing shared Data would ripple across all charts, so cast here.
      const rows = data.rows.map((r) => {
        const deltaKey = `${curr}:delta`;
        const delta = r.cols[deltaKey as keyof typeof r.cols]?.aggregate ?? null;

        return {
          id: r.id,
          name: r.name,
          legend: label || r.name,
          data: r.cols[curr].values.map((v: number | null, i: number) => {
            const granularity = data.periods[i].granularity;
            const periodEnd = data.periods[i].end;

            return {
              value: v,
              granularity: granularity,
              date: periodEnd,
              label: label || r.name,
              periodLabel: getPeriodLabel(granularity, periodEnd),
              delta,
            };
          }),
        };
      });
      prev[curr] = rows as ChartValues[];
      return prev;
    },
    {} as StatisticsChartData<Series>,
  );

  return chartData;
};
